import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FieldArray, FieldArrayPath, FieldPathByValue, FieldValues, Path, PathValue, useFormContext,
} from 'react-hook-form'
import { ISelectOption } from 'types/IClassifier'
import { PreviousDocument } from 'features/declaration/models/schemas/documentSchemas'
import { FileType } from 'features/declaration/models/schemas/fileSchemas'
import { PREVIOUS_DOCUMENT_TYPE_CODE_PREFIX, removePrefix } from 'shared/ncts/common/classifier-util'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import FormField from 'components/p5/FormField'
import FormFieldArray from 'components/p5/FormFieldArray'
import FormSelect from 'components/p5/FormSelect'
import Input from 'components/p5/Input'

import DocumentFiles from 'features/declaration/document/components/DocumentFiles'
import DocumentDropdownArea from 'features/declaration/document/components/DocumentDropdownArea'
import CustomValidators from 'features/declaration/form/validations/customValidators'
import dotLeafPaths from 'helpers/dot-leaf-paths'
import { ArrayPathByValue } from 'shared/rhf/models/types'
import { NctsScope } from 'shared/ncts/types'
import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'

type FieldValuesWithPreviousDocuments = FieldValues & {
  previousDocument: PreviousDocument[]
}

const scope = 'previousDocument'

type PreviousDocumentRelPath = dotLeafPaths<PreviousDocument>
type PreviousDocumentBasePath<TForm extends FieldValuesWithPreviousDocuments> = Extract<Path<TForm>, `${typeof scope}.${number}`>
type PreviousDocumentFilesArrayPath<TForm extends FieldValuesWithPreviousDocuments> = Extract<Path<TForm>,
  `${typeof scope}.${number}.files`
>
type FileArrayPath<TForm extends FieldValues> = Extract<FieldArrayPath<TForm>, FieldPathByValue<TForm, FileType[]>>

type PreviousDocumentsArrayPath<TForm extends FieldValues> = ArrayPathByValue<TForm, PreviousDocument>

type PreviousDocumentsProps<TForm extends FieldValuesWithPreviousDocuments> = {
  scope: NctsScope
  createDefault: () => FieldArray<TForm, PreviousDocumentsArrayPath<TForm>>
  country: string
}

function PreviousDocuments<
  TForm extends FieldValuesWithPreviousDocuments,
>({
  scope: formScope,
  createDefault,
  country,
}: Readonly<PreviousDocumentsProps<TForm>>) {
  const { t } = useTranslation()

  const { control, getValues, setValue } = useFormContext<TForm>()
  const {
    visibleFields,
    softRemove,
    appendWithSequence,
  } = useSoftDeleteFieldArray<TForm, PreviousDocument, PreviousDocumentsArrayPath<TForm>>({
    control,
    name: scope as PreviousDocumentsArrayPath<TForm>,
  })

  const paths = useMemo(
    () => [
      scope,
      ...visibleFields.map((
        f,
      ) => `${scope}.${f.index}.documentType`),
      ...visibleFields.map((
        f,
      ) => `${scope}.${f.index}.complementOfInformation`),
      ...visibleFields.map((
        f,
      ) => `${scope}.${f.index}.procedureType`),
      ...visibleFields.map((
        f,
      ) => `${scope}.${f.index}.referenceNumber`),
      ...visibleFields.map((
        f,
      ) => `${scope}.${f.index}.transportDocumentReferenceNumber`),
    ],
    [visibleFields],
  )

  const fieldRequirements = useDeclarationFieldRequirements<TForm>(formScope, paths)

  const [, previousDocumentTypesOptions] = useCodelist('PREVIOUS_DOCUMENTS')

  const [procedureTypeOptions] = useState<ISelectOption[]>([
    {
      label: 'PIIR - Piiriületus',
      value: 'PIIR',
    },
    {
      label: 'ASENDUS - Asendustoiming',
      value: 'ASENDUS',
    },
    {
      label: 'VABA - Vabatsoon',
      value: 'VABA',
    },
  ])

  const isConsignmentItemPreviousDocument = formScope === 'consignmentItem'
  const isHouseConsignmentPreviousDocument = formScope === 'houseConsignment'

  const documentTypeOptions = useMemo(
    () => previousDocumentTypesOptions
      .filter((item) => {
        const isExportDocument = removePrefix(item.value, PREVIOUS_DOCUMENT_TYPE_CODE_PREFIX) === 'N830'

        return (isExportDocument && isHouseConsignmentPreviousDocument) || (!isExportDocument && !isHouseConsignmentPreviousDocument)
      }),
    [previousDocumentTypesOptions, isHouseConsignmentPreviousDocument],
  )

  const removeDocument = useCallback((index: number) => {
    const innerItemScope = `${scope}.${index}` as PreviousDocumentBasePath<TForm>
    const filesPath = `${innerItemScope}.files` as PreviousDocumentFilesArrayPath<TForm>
    const filesOfRemovedDocument = getValues(filesPath)
    const updatedFiles = filesOfRemovedDocument.map((f) => (
      {
        ...f,
        deleted: true,
      }
    ))
    setValue(filesPath, updatedFiles as PathValue<TForm, typeof filesPath>)

    softRemove(index)
  }, [softRemove, getValues, setValue])

  const getValueAtIndex = (index: number, relPath: PreviousDocumentRelPath) => {
    const fullPath = `previousDocument.${index}.${relPath}` as Path<TForm>

    return getValues(fullPath)
  }

  const isAddForbidden = fieldRequirements.previousDocument?.requirement === 'forbidden'

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-previous-documents"
        headingLabel={t('declaration.p5.previousDocuments')}
        fieldRule={fieldRequirements.previousDocument}
      >
        {visibleFields.map((fieldWithIndex) => {
          const previousDocument = fieldWithIndex.field
          const innerItemScope = `${scope}.${fieldWithIndex.index}` as PreviousDocumentBasePath<TForm>
          const filesPath = `${innerItemScope}.files` as FileArrayPath<TForm>
          const documentType = getValueAtIndex(fieldWithIndex.index, 'documentType')
          const documentId = getValueAtIndex(fieldWithIndex.index, 'id')
          const isTransportDocumentNumberVisible = CustomValidators
            .validateTransportDocumentReferenceNumberRequirement(documentType, country)

          return (
            <FormFieldArray
              key={`previousDocument_${previousDocument.id}`}
              titleKey="declaration.p5.sequenceNumber"
              removeItem={() => removeDocument(fieldWithIndex.index)}
              sequenceNumber={previousDocument.sequenceNumber + 1}
              hidden={previousDocument.deleted}
            >
              <FormField
                labelKey="declaration.p5.previousDocumentType"
                fieldRule={fieldRequirements[`${innerItemScope}.documentType`]}
              >
                <FormSelect
                  <TForm>
                  field={`${innerItemScope}.documentType` as Path<TForm>}
                  labelKey="declaration.p5.previousDocumentType"
                  options={documentTypeOptions}
                  type="sync"
                  fieldRule={fieldRequirements[`${innerItemScope}.documentType`]}
                />
              </FormField>
              {country === 'EE' && !isConsignmentItemPreviousDocument && (
                <FormField
                  labelKey="declaration.p5.procedureType"
                  fieldRule={fieldRequirements[`${innerItemScope}.procedureType`]}
                >
                  <FormSelect
                    <TForm>
                    field={`${innerItemScope}.procedureType` as Path<TForm>}
                    labelKey="declaration.p5.procedureType"
                    options={procedureTypeOptions}
                    type="sync"
                    fieldRule={fieldRequirements[`${innerItemScope}.procedureType`]}
                  />
                </FormField>
              )}
              <FormField
                labelKey="declaration.p5.previousDocumentReferenceNumber"
                fieldRule={fieldRequirements[`${innerItemScope}.referenceNumber`]}
              >
                <Input
                  type="text"
                  field={`${innerItemScope}.referenceNumber`}
                  labelKey="declaration.p5.previousDocumentReferenceNumber"
                  fieldRule={fieldRequirements[`${innerItemScope}.referenceNumber`]}
                />
              </FormField>
              <FormField
                labelKey="declaration.p5.complementOfInformation"
                fieldRule={fieldRequirements[`${innerItemScope}.complementOfInformation`]}
              >
                <Input
                  type="text"
                  field={`${innerItemScope}.complementOfInformation`}
                  labelKey="declaration.p5.complementOfInformation"
                  fieldRule={fieldRequirements[`${innerItemScope}.complementOfInformation`]}
                />
              </FormField>
              {
                isTransportDocumentNumberVisible && (
                  <FormField
                    labelKey="declaration.p5.transportDocumentReferenceNumber"
                    fieldRule={fieldRequirements[`${innerItemScope}.transportDocumentReferenceNumber`]}
                  >
                    <Input
                      type="text"
                      field={`${innerItemScope}.transportDocumentReferenceNumber`}
                      labelKey="declaration.p5.transportDocumentReferenceNumber"
                      fieldRule={fieldRequirements[`${innerItemScope}.transportDocumentReferenceNumber`]}
                    />
                  </FormField>
                )
              }
              <DocumentFiles<TForm>
                key={`documentFileNames${previousDocument.id + previousDocument.sequenceNumber}`}
                name={filesPath}
              />
              <DocumentDropdownArea
                key={`documents${previousDocument.sequenceNumber + previousDocument.id}`}
                name={filesPath}
                country={country}
                documentId={documentId}
              />
            </FormFieldArray>
          )
        })}
        <div className="row">
          <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={() => appendWithSequence(createDefault())}
              disabled={isAddForbidden}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewDocument')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default PreviousDocuments
